/**
 * ClientsDesktop — десктоп-раскладка раздела «Клиенты» по макету:
 * свой sticky-хедер (счётчик, колокол, +Создать), тулбар (поиск + 4
 * фильтра), карточка-таблица с пагинацией.
 */

import {Bell, ChevronLeft, ChevronRight, Plus} from 'lucide-react';

import {type ClientListItem, TARIFF_OPTIONS} from '@services/clients';
import {useCityOptions, useStatusOptions} from '@hooks/useFilterRefs';
import {SearchField} from '@components/SearchField';
import {Button} from '@components/ui/Button';
import {Loader} from '@components/ui/Loader';
import {formatNumber} from '@utils/format';

import {type ClientsFilters, TYPE_OPTIONS} from '../Clients.utils';
import {ClientsTable} from '../ClientsTable';
import {FilterMulti, FilterSelect} from '../FilterControls';

type ClientsDesktopProps = {
  filters: ClientsFilters;
  patch: (p: Partial<ClientsFilters>) => void;
  rows: ClientListItem[];
  totalCount: number;
  pageSize: number;
  isLoading: boolean;
  onOpenClient: (id: string) => void;
  onCreate: () => void;
};

export const ClientsDesktop = ({
  filters,
  patch,
  rows,
  totalCount,
  pageSize,
  isLoading,
  onOpenClient,
  onCreate,
}: ClientsDesktopProps) => {
  const pages = Math.max(1, Math.ceil(totalCount / pageSize));
  const statusOptions = useStatusOptions();
  const cityOptions = useCityOptions();

  return (
    <>
      <header className="sticky top-0 z-10 flex items-center gap-4 border-b border-skr-line bg-background/80 px-[30px] py-[18px] backdrop-blur">
        <div className="min-w-0 flex-1">
          <h1 className="text-[21px] font-bold -tracking-[.02em]">Клиенты</h1>
          <div className="mt-0.5 text-[13px] text-muted-foreground">
            {formatNumber(totalCount)} клиентов · Соло и клиники
          </div>
        </div>
        <Button variant="outline" size="icon" aria-label="Уведомления">
          <Bell />
        </Button>
        <Button onClick={onCreate}>
          <Plus />
          Создать аккаунт
        </Button>
      </header>

      <div className="flex flex-col gap-4 px-[30px] pb-10 pt-[22px]">
        {/* toolbar */}
        <div className="flex flex-wrap items-center gap-2.5">
          <SearchField
            value={filters.search}
            onChange={(v) => patch({search: v, page: 1})}
            placeholder="Название, имя или телефон…"
            className="h-10 min-w-[240px] max-w-[360px] flex-1 rounded-[10px] border border-input bg-card px-[13px]"
          />
          <div className="flex-1" />
          <FilterSelect
            label="Статус"
            options={statusOptions}
            value={filters.status}
            onChange={(v) => patch({status: (v as ClientsFilters['status']) ?? null, page: 1})}
          />
          <FilterSelect
            label="Тип"
            options={TYPE_OPTIONS}
            value={filters.type}
            onChange={(v) => patch({type: (v as ClientsFilters['type']) ?? null, page: 1})}
          />
          <FilterMulti
            label="Тариф"
            options={TARIFF_OPTIONS}
            selected={filters.tariffs}
            onChange={(v) => patch({tariffs: v, page: 1})}
          />
          <FilterMulti
            label="Город"
            options={cityOptions}
            selected={filters.cities}
            onChange={(v) => patch({cities: v, page: 1})}
          />
        </div>

        {/* table card */}
        <div className="overflow-hidden rounded-[14px] border border-skr-line bg-card shadow-[0_1px_2px_rgba(20,25,40,.03)]">
          {isLoading ? (
            <div className="flex min-h-[352px] items-center justify-center">
              <Loader label="Загружаем клиентов…" />
            </div>
          ) : (
            <ClientsTable rows={rows} onOpenClient={onOpenClient} />
          )}

          <div className="flex items-center justify-between border-t border-skr-line2 px-[18px] py-3.5">
            <div className="text-[13px] text-muted-foreground">
              Показано <span className="tnum font-mono text-skr-ink2">{rows.length}</span> из{' '}
              <span className="tnum font-mono text-skr-ink2">{formatNumber(totalCount)}</span>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="icon"
                className="size-[34px]"
                disabled={filters.page <= 1}
                onClick={() => patch({page: filters.page - 1})}
                aria-label="Назад"
              >
                <ChevronLeft />
              </Button>
              <Button
                variant="outline"
                size="icon"
                className="size-[34px]"
                disabled={filters.page >= pages}
                onClick={() => patch({page: filters.page + 1})}
                aria-label="Вперёд"
              >
                <ChevronRight />
              </Button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
